import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Plus,
  Search,
  X as CloseIcon,
  Calendar,
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
} from "lucide-react";

// H Components
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import HStatCard from "../../../hcomponents/HStatCard";
import HCreateAnnouncementModal from "../../../hcomponents/HCreateAnnouncementModal";
import HEditAnnouncementModal from "../../../hcomponents/HEditAnnouncementModal";
import HEmptyState from "../../../hcomponents/HEmptyState";
import HPagination from "../../../hcomponents/HPagination";

// Shadcn Components
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { Badge } from "../../../shadcn/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shadcn/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../shadcn/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";

// Utils
import { showApiMessage } from "../../../utils/messageHandler";
import apiService from "../../../services/apiService";
import logger from "../../../utils/logger";
import moment from "moment";
import * as localService from "../../../services/localService";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import { useOrganizationRoles } from "../../../hooks/useOrganizationRoles";

const HostAnnouncements = () => {
  const history = useHistory();

  // Get current user for feature restrictions
  const currentUser = localService.getUser();

  // Use centralized feature access control
  const canCreateAnnouncement = FeatureAccess.canCreateAnnouncement(currentUser);

  // Load organization roles eagerly
  const { organizationRoles } = useOrganizationRoles();
  logger.log("HostAnnouncements entry - Roles:", organizationRoles.length);

  // State management
  const [activeTab, setActiveTab] = useState("created"); // "created" or "all"
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;

  // Helper functions to transform API data to match our UI expectations
  const transformAnnouncementData = useCallback(apiData => {
    return apiData.map(announcement => ({
      id: announcement.id,
      title: announcement.title,
      message: announcement.body,
      status: getAnnouncementStatus(announcement.datetimeToSend),
      targetRoles: announcement.roles || [],
      createdAt: formatApiDate(announcement.createdAt),
      scheduledFor: announcement.datetimeToSend ? formatApiDate(announcement.datetimeToSend) : null,
      priority: announcement.priority?.toLowerCase() || "medium",
      datetimeToSend: announcement.datetimeToSend,
      originalData: announcement,
    }));
  }, []);

  const getAnnouncementStatus = datetimeToSend => {
    if (!datetimeToSend) return "draft";

    // Handle both old format (object) and new format (ISO string)
    let publishDate;
    if (typeof datetimeToSend === "string") {
      publishDate = moment.utc(datetimeToSend);
    } else {
      // Old format with year, month, etc.
      publishDate = moment.utc({
        year: datetimeToSend.year,
        month: datetimeToSend.monthValue - 1,
        date: datetimeToSend.dayOfMonth,
        hour: datetimeToSend.hour,
        minute: datetimeToSend.minute,
        second: datetimeToSend.second,
      });
    }

    const now = moment.utc();
    return publishDate.isAfter(now) ? "scheduled" : "published";
  };

  const formatApiDate = apiDate => {
    if (!apiDate) return null;

    // Handle both old format (object) and new format (ISO string)
    if (typeof apiDate === "string") {
      return apiDate; // Already an ISO string
    } else {
      // Old format with year, month, etc.
      return moment({
        year: apiDate.year,
        month: apiDate.monthValue - 1,
        date: apiDate.dayOfMonth,
        hour: apiDate.hour,
        minute: apiDate.minute,
        second: apiDate.second,
      }).toISOString();
    }
  };

  // Load data on component mount and when page or tab changes
  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage, activeTab]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      logger.log("Fetching announcements...");

      const user = localService.getUser();
      const userId = user?.id || user?.userId || user?.user?.id;

      let response;
      if (activeTab === "created") {
        // Use the correct method for notifications created by the host
        response = await apiService.getCreatedNotificationsByMe(currentPage + 1, pageSize, "createdAt:desc", "desc");
      } else {
        // "all" tab maps to organization notifications
        response = await apiService.getCreatedNotifications(currentPage + 1, pageSize, "createdAt:desc", "desc");
      }
      logger.log("Raw announcements response:", response);

      let rawData = [];
      let totalCount = 0;

      if (response && response.data) {
        // 1. Handle nested notifications (as seen in AuthenticatedLayout)
        if (
          response.data.privateNotifications ||
          response.data.publicNotifications ||
          response.data.roleBasedNotifications
        ) {
          const privateNotifs = response.data.privateNotifications || [];
          const publicNotifs = response.data.publicNotifications || [];
          const roleBasedNotifs = response.data.roleBasedNotifications || [];
          rawData = [...privateNotifs, ...publicNotifs, ...roleBasedNotifs];
          totalCount = rawData.length;
        }
        // 2. Handle direct array
        else if (Array.isArray(response.data)) {
          rawData = response.data;
          totalCount = response.data.length;
        }
        // 3. Handle paged response (content property)
        else if (response.data.content && Array.isArray(response.data.content)) {
          rawData = response.data.content;
          totalCount =
            response.data.totalElements !== undefined
              ? response.data.totalElements
              : response.data.total !== undefined
                ? response.data.total
                : rawData.length;
        }
        // 4. Handle nested data property
        else if (response.data.data && Array.isArray(response.data.data)) {
          rawData = response.data.data;
          totalCount = response.data.totalElements || response.data.total || rawData.length;
        }
      }
      // Handle when response itself is an array
      else if (Array.isArray(response)) {
        rawData = response;
        totalCount = response.length;
      }

      if (rawData.length >= 0) {
        const transformedData = transformAnnouncementData(rawData);
        setAnnouncements(transformedData);

        // Set pagination info
        if (response?.data?.totalPages) {
          setTotalPages(response.data.totalPages);
        } else if (totalCount > 0) {
          setTotalPages(Math.ceil(totalCount / pageSize));
        } else {
          setTotalPages(0);
        }
      } else {
        logger.error("Invalid response format:", response);
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Invalid data received from server" },
            },
          },
          "fetch announcements"
        );
      }

      setLoading(false);
    } catch (error) {
      logger.error("Error fetching announcements:", error);
      setLoading(false);
      showApiMessage(error, "load announcements");
    }
  };

  // Custom debounce hook for search
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Memoized filter to prevent unnecessary re-calculations
  const filteredAnnouncements = useMemo(() => {
    return announcements.filter(announcement => {
      const matchesSearch =
        debouncedSearchQuery === "" ||
        announcement.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        announcement.message.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || announcement.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [announcements, debouncedSearchQuery, statusFilter]);

  // Handle actions with useCallback to prevent unnecessary re-renders
  const handleCreateAnnouncement = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleEditAnnouncement = useCallback(announcement => {
    setSelectedAnnouncement(announcement);
    setShowEditModal(true);
  }, []);

  const handleViewAnnouncement = useCallback(
    announcement => {
      // Navigate to detailed view
      history.push(`/host/announcements/${announcement.id}`);
    },
    [history]
  );

  const handleDeleteAnnouncement = useCallback(announcement => {
    setAnnouncementToDelete(announcement);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteAnnouncement = useCallback(async () => {
    if (!announcementToDelete) return;

    try {
      const resp = await apiService.deleteAnnouncement(announcementToDelete.id);
      showApiMessage(resp, "delete announcement");
      fetchAnnouncements(); // Refresh the list

      setShowDeleteConfirm(false);
      setAnnouncementToDelete(null);
    } catch (error) {
      logger.error("Error deleting announcement:", error);
      showApiMessage(error, "delete announcement");
      setShowDeleteConfirm(false);
      setAnnouncementToDelete(null);
    }
  }, [announcementToDelete, fetchAnnouncements]);

  const getStatusBadge = useCallback(status => {
    switch (status) {
      case "published":
        return <Badge className="text-green-700 bg-green-100">Published</Badge>;
      case "scheduled":
        return <Badge className="text-blue-700 bg-blue-100">Scheduled</Badge>;
      case "draft":
        return <Badge className="text-gray-700 bg-gray-100">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  }, []);

  const getPriorityColor = useCallback(priority => {
    switch (priority) {
      case "high":
        return "text-red-600";
      case "medium":
        return "text-yellow-600";
      case "low":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  }, []);

  const formatDate = useCallback(dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const addNewButton = useMemo(
    () =>
      canCreateAnnouncement ? (
        <HActionButton
          icon={<Plus className="w-4 h-4" />}
          label="New Announcement"
          onClick={handleCreateAnnouncement}
          variant="primary"
        />
      ) : null,
    [handleCreateAnnouncement, canCreateAnnouncement]
  );

  if (loading) {
    return (
      <HPageContainer>
        <HHeader rightButton={addNewButton} />
        <div className="overflow-auto flex-1 p-4 sm:p-6 md:p-8">
          <div className="space-y-6 animate-pulse">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  return (
    <HPageContainer>
      <HHeader rightButton={addNewButton} />

      <div className="overflow-auto flex-1 p-4 sm:p-6 md:p-8">
        <div className="mb-6">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-max">
            <button
              onClick={() => {
                setActiveTab("created");
                setCurrentPage(0);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                activeTab === "created" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              My Created
            </button>
            <button
              onClick={() => {
                setActiveTab("all");
                setCurrentPage(0);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                activeTab === "all" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Organization
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col gap-4 mb-6 sm:flex-row">
          <div className="relative flex-1">
            <Input
              placeholder="Search announcements..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
              {searchQuery ? (
                <CloseIcon
                  className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
                  onClick={() => setSearchQuery("")}
                />
              ) : (
                <Search className="w-4 h-4 text-gray-400 pointer-events-none" />
              )}
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Announcements List */}
        {filteredAnnouncements.length === 0 ? (
          <HEmptyState
            icon={Users}
            title="No announcements found"
            description="Create your first announcement to communicate with your users"
            actionLabel="Create Announcement"
            onAction={handleCreateAnnouncement}
          />
        ) : (
          <div className="space-y-4">
            {filteredAnnouncements.map(announcement => (
              <Card key={announcement.id} className="transition-shadow hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex gap-3 items-center mb-2">
                        <CardTitle className="text-lg">{announcement.title}</CardTitle>
                        {getStatusBadge(announcement.status)}
                        <div
                          className={`w-2 h-2 rounded-full ${getPriorityColor(announcement.priority).replace("text-", "bg-")}`}
                          title={`${announcement.priority} priority`}
                        />
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{announcement.message}</p>
                    </div>

                    {activeTab === "mine" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditAnnouncement(announcement)}>
                            <Edit className="mr-2 w-4 h-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteAnnouncement(announcement)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 w-4 h-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="flex flex-wrap gap-4 items-center text-sm text-gray-500">
                    <div className="flex gap-1 items-center">
                      <Calendar className="w-4 h-4" />
                      <span>Created: {formatDate(announcement.createdAt)}</span>
                    </div>

                    {announcement.scheduledFor && (
                      <div className="flex gap-1 items-center">
                        <Clock className="w-4 h-4" />
                        <span>Scheduled: {formatDate(announcement.scheduledFor)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        <HPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {/* Modals */}
      <HCreateAnnouncementModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchAnnouncements}
      />

      <HEditAnnouncementModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        announcement={selectedAnnouncement}
        onSuccess={fetchAnnouncements}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Announcement</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this announcement? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setAnnouncementToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmDeleteAnnouncement} className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
};

export default HostAnnouncements;
